import { useCallback, useMemo } from 'react';
import { ValidationError, fetchData } from 'utils';
import {
  ERRORS_TEMPLATES,
  isErrorTemplate,
} from 'utils/errorMessageValidation';
import t from 'utils/translate';
import useError from 'hooks/useError';
import useSmartlook from 'hooks/useSmartlook';

const usePhoneVerification = () => {
  const { showError } = useError();
  const { error } = useSmartlook();

  const requestPhoneVerification = useCallback(
    async (mobilePhone: string) => {
      try {
        const data = await fetchData('/client/phone', {
          method: 'post',
          body: JSON.stringify({ mobilePhone }),
        });

        return data;
      } catch (e) {
        error('[usePhoneVerification][requestPhoneVerification]', e);
        showError();
        throw e;
      }
    },
    [showError],
  );

  const changePhoneWithVerification = useCallback(
    async (mobilePhone: string) => {
      try {
        const data = await fetchData('/client/phone', {
          method: 'put',
          body: JSON.stringify({ mobilePhone }),
        });

        return data;
      } catch (e) {
        error('[usePhoneVerification][changePhoneWithVerification]', e);
        if (e instanceof ValidationError) {
          showError();
        }
        throw e;
      }
    },
    [showError],
  );

  const requestPhoneCallVerification = useCallback(async () => {
    try {
      const data = await fetchData('/client/phone/call', {
        method: 'post',
      });

      return data;
    } catch (e) {
      error('[usePhoneVerification][requestPhoneVerification]', e);
      showError();
      throw e;
    }
  }, [showError]);

  const sendCode = useCallback(
    async (verificationCode: string) => {
      try {
        const data = await fetchData('/client/phone-confirmation', {
          method: 'put',
          body: JSON.stringify({ verificationCode }),
        });

        return data;
      } catch (e) {
        error('[usePhoneVerification][sendCode]', e);

        if (e instanceof ValidationError) {
          // Incorrect code error managed externally
          const isIncorrectCode = isErrorTemplate(
            e,
            ERRORS_TEMPLATES.INCORRECT_CODE,
          );

          if (!isIncorrectCode) {
            const messageTemplate =
              e.fieldErrors && e.fieldErrors[0]?.messageTemplate;
            const errorMsgExists =
              messageTemplate &&
              !t(`error.validation.${messageTemplate}`).includes('{');

            showError(
              errorMsgExists
                ? { message: t(`error.validation.${messageTemplate}`) }
                : {},
            );
          }
        }
        throw e;
      }
    },
    [showError],
  );

  return useMemo(() => {
    return {
      changePhoneWithVerification,
      requestPhoneVerification,
      sendCode,
      requestPhoneCallVerification,
    };
  }, [changePhoneWithVerification, requestPhoneVerification, sendCode]);
};

export default usePhoneVerification;
